/**
 * ClassTemplate Card Component
 *
 * A card-based display for class template information, designed for mobile-friendly staff viewing.
 * Shows class name, schedule, swim level, capacity, and pricing.
 */

import {
  Badge,
  Box,
  Card,
  Flex,
  HStack,
  Icon,
  Progress,
  Text,
  VStack,
} from "@chakra-ui/react"
import {
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiMapPin,
  FiUsers,
} from "react-icons/fi"

import { ClassTemplateActionsMenu } from "@/components/layout/Common/ClassTemplateActionsMenu"
import type { ClassTemplateResponse } from "@/types/class_template"

// Swim level colors (shared with StudentCard)
const swimLevelColors: Record<string, string> = {
  Bubble: "blue",
  "Tot (Ages 3-4 years)": "purple",
  Star: "yellow",
  Kicker: "green",
  Roller: "teal",
  Puller: "cyan",
  Charger: "orange",
  "Delta (Level 9 - Level 12)": "red",
  "Gamma (Level 13 - Level 16)": "pink",
  Challenge: "gray",
  "Intermediate(was Pre-Competitive)": "blue",
  "Advance (was Competitive)": "purple",
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount)
}

export interface ClassTemplateCardProps {
  classTemplate: ClassTemplateResponse
  /** Whether to show action menu */
  showActions?: boolean
  /** Click handler for the card */
  onClick?: () => void
}

export function ClassTemplateCard({
  classTemplate,
  showActions = true,
  onClick,
}: ClassTemplateCardProps) {
  const isActive = classTemplate.is_active !== false
  const isPublished = classTemplate.is_published !== false
  const status = !isActive ? "Inactive" : !isPublished ? "Draft" : "Published"
  const statusColor = !isActive ? "gray" : !isPublished ? "orange" : "green"

  const currentEnrollment = classTemplate.current_enrollment || 0
  const maxStudents = classTemplate.max_students
  const utilization =
    maxStudents > 0 ? (currentEnrollment / maxStudents) * 100 : 0
  const capacityColor =
    utilization >= 100 ? "red" : utilization >= 80 ? "orange" : "green"

  const levelColor = classTemplate.swim_level
    ? swimLevelColors[classTemplate.swim_level] || "gray"
    : "gray"

  return (
    <Card.Root
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      borderWidth="1px"
      borderColor="gray.100"
      _hover={{
        boxShadow: "md",
        borderColor: "ui.main",
        transform: onClick ? "translateY(-2px)" : undefined,
      }}
      transition="all 0.2s"
      cursor={onClick ? "pointer" : "default"}
      onClick={onClick}
    >
      <Card.Body p={5}>
        <VStack align="stretch" gap={4}>
          {/* Header with class name and status */}
          <Flex justify="space-between" align="start">
            <Box flex={1}>
              <Text fontWeight="semibold" fontSize="lg" color="gray.800">
                {classTemplate.name}
              </Text>
              <HStack gap={2} mt={1}>
                <Badge
                  colorPalette={statusColor}
                  variant="subtle"
                  size="sm"
                  borderRadius="full"
                >
                  {status}
                </Badge>
                <Badge
                  colorPalette={levelColor}
                  variant="subtle"
                  size="sm"
                  borderRadius="full"
                >
                  {classTemplate.swim_level}
                </Badge>
              </HStack>
            </Box>

            {/* Actions menu */}
            {showActions && (
              <Box onClick={(e) => e.stopPropagation()}>
                <ClassTemplateActionsMenu classTemplate={classTemplate} />
              </Box>
            )}
          </Flex>

          {/* Schedule + Location */}
          <VStack align="stretch" gap={2}>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiCalendar />
              </Icon>
              <Text>{classTemplate.day_of_week}</Text>
            </HStack>
            <HStack fontSize="sm" color="gray.600">
              <Icon color="gray.400">
                <FiClock />
              </Icon>
              <Text>
                {classTemplate.start_time}
                {classTemplate.end_time && ` - ${classTemplate.end_time}`}
              </Text>
            </HStack>
            {classTemplate.location && (
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiMapPin />
                </Icon>
                <Text>{classTemplate.location}</Text>
              </HStack>
            )}
          </VStack>

          {/* Capacity bar */}
          <Box>
            <Flex justify="space-between" align="center" mb={1}>
              <HStack fontSize="sm" color="gray.600">
                <Icon color="gray.400">
                  <FiUsers />
                </Icon>
                <Text fontWeight="medium">Capacity</Text>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                {currentEnrollment}/{maxStudents}
              </Text>
            </Flex>
            <Progress.Root
              value={Math.min(utilization, 100)}
              size="sm"
              colorPalette={capacityColor}
            >
              <Progress.Track>
                <Progress.Range />
              </Progress.Track>
            </Progress.Root>
          </Box>

          {/* Price + Program type */}
          <Flex justify="space-between" align="center">
            <Badge variant="outline" size="sm">
              {classTemplate.program_type}
            </Badge>
            <HStack fontSize="sm" color="gray.700">
              <Icon color="gray.400">
                <FiDollarSign />
              </Icon>
              <Text fontWeight="semibold">
                {formatCurrency(classTemplate.price)}
              </Text>
            </HStack>
          </Flex>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

export default ClassTemplateCard
